/**
 * Media type constants for file validation in chat.
 * Used by FileValidationService and file upload components.
 */

export const DocumentMediaType: string[] = [
  "plain",
  "pdf",
  "rtf",
  "vnd.ms-powerpoint",
  "msword",
  "vnd.ms-excel",
  "vnd.openxmlformats-officedocument.wordprocessingml.document",
  "vnd.openxmlformats-officedocument.presentationml.presentation",
  "vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  "vnd.oasis.opendocument.spreadsheet",
  "vnd.oasis.opendocument.presentation",
  "comma-separated-values",
  "csv",
]

export const ImageMediaType: string[] = ["jpeg", "png"]

export const LazadaImageMediaType: string[] = ["jpeg", "png", "gif"]

export const VideoMediaType: string[] = ["mp4", "3gp"]

export const StickerMediaType: string[] = ["webp"]

export const AudioMediaType: string[] = ["aac", "mp4", "mpeg", "amr", "ogg"]

/**
 * File size limits in MB
 * Phase 2: Unified limits across all platforms
 * - Image: 4.75MB (95% of 5MB WhatsApp limit for safety buffer)
 * - Video: 16MB
 * - Document: 100MB (WhatsApp only)
 * - Audio: 16MB (WhatsApp only)
 */
export const FileSizeLimits = {
  document: 100,
  image: 4.75, // Unified: 4980736 bytes (95% of 5MB WhatsApp limit)
  video: 16,
  audio: 16,
} as const

/**
 * Phase 2: Image dimension limits
 * Must match backend validation in ImageValidationService
 */
export const ImageDimensionLimits = {
  maxWidth: 5000,
  maxHeight: 5000,
  minWidth: 50,
  minHeight: 50,
  maxAspectRatio: 10, // 10:1 or 1:10
} as const

/**
 * Validate image dimensions (Phase 2)
 * Returns error code if invalid, undefined if valid
 */
export function validateImageDimensions(
  width: number,
  height: number
): string | undefined {
  if (width > ImageDimensionLimits.maxWidth || height > ImageDimensionLimits.maxHeight) {
    return "DimensionsExceedLimit"
  }

  if (width < ImageDimensionLimits.minWidth || height < ImageDimensionLimits.minHeight) {
    return "DimensionsTooSmall"
  }

  const aspectRatio = width / height
  if (
    aspectRatio > ImageDimensionLimits.maxAspectRatio ||
    aspectRatio < 1 / ImageDimensionLimits.maxAspectRatio
  ) {
    return "AspectRatioInvalid"
  }

  return undefined
}

/**
 * Get file type from MIME type
 */
export function getFileTypeFromMime(mimeType: string): string | undefined {
  const type = mimeType.toLowerCase()

  if (type === "application" || type === "text") {
    return "document"
  }
  if (type === "image") {
    return "image"
  }
  if (type === "video") {
    return "video"
  }
  if (type === "audio") {
    return "audio"
  }

  return undefined
}

/**
 * Check if file extension is valid for the given type
 */
export function isValidExtension(
  fileType: string,
  extension: string,
  channel?: string
): boolean {
  switch (fileType) {
    case "document":
      return DocumentMediaType.includes(extension)
    case "image":
      if (channel === "lazada") {
        return LazadaImageMediaType.includes(extension)
      }
      return ImageMediaType.includes(extension)
    case "video":
      return VideoMediaType.includes(extension)
    case "audio":
      return AudioMediaType.includes(extension)
    default:
      return false
  }
}

/**
 * Validates a file based on type, extension, and size.
 * Returns [fileType, error, maxSize]
 */
export const checkFile = (
  FileType: string | undefined,
  FileExtensionType: string | undefined,
  FileSize: number,
  FileChannel?: string
): [string | undefined, string | undefined, number] => {
  let result: string | undefined = undefined
  let error: string | undefined = undefined
  let maxSize = 0

  // Check File Type
  if (FileType === undefined) {
    error = "InvalidURL"
  } else if (FileType.toLowerCase() === "application" || FileType.toLowerCase() === "text") {
    result = "document"
  } else if (FileType.toLowerCase() === "image") {
    result = "image"
  } else if (FileType.toLowerCase() === "video") {
    result = "video"
  } else if (FileType.toLowerCase() === "audio") {
    result = "audio"
  } else {
    error = "InvalidURL"
  }

  // Check File Size
  if (result !== undefined) {
    const sizeInMB = FileSize / (1024 * 1024)
    if (result === "document" && sizeInMB >= FileSizeLimits.document) {
      maxSize = FileSizeLimits.document
      error = "MaxSize"
    }
    if (result === "image" && sizeInMB >= FileSizeLimits.image) {
      maxSize = FileSizeLimits.image
      error = "MaxSize"
    }
    if (result === "video" && sizeInMB >= FileSizeLimits.video) {
      maxSize = FileSizeLimits.video
      error = "MaxSize"
    }
    if (result === "audio" && sizeInMB >= FileSizeLimits.audio) {
      maxSize = FileSizeLimits.audio
      error = "MaxSize"
    }
  }

  // Check File Extension
  if (result !== undefined && FileExtensionType !== undefined) {
    if (!isValidExtension(result, FileExtensionType, FileChannel)) {
      error = "InvalidFiletype"
    }
  }

  return [result, error, maxSize]
}

/**
 * Formats file size in bytes to human-readable string.
 */
export const formatSize = (bytes: number, decimals = 2): string => {
  if (bytes === 0) return "0 Bytes"

  const k = 1024
  const dm = decimals < 0 ? 0 : decimals
  const sizes = ["Byte", "KB", "MB", "GB", "TB", "PB", "EB", "ZB", "YB"]

  const i = Math.floor(Math.log(bytes) / Math.log(k))

  return parseFloat((bytes / Math.pow(k, i)).toFixed(dm)) + " " + sizes[i]
}
